import React from 'react';
import styled from 'styled-components';

import { AdminListPageConfig, AdminListPageRenderer, useAdminListPage } from 'hooks/useAdminListPage';
import { DischargeReason } from 'models/flexible/DischargeReason';
import {
  adminArchiveDischargeReason,
  adminDischargeReasonsQueryKeys,
  adminIndexDischargeReasons,
} from 'services/api/admin/dischargeReasons';
import { indexGroupTypes } from 'services/api/admin/groupTypes';
import type { AdminDischargeReasonIndexFields } from 'services/api/fields';

const INCLUDES = ['dischargedToGroupType', 'groupTypeDisplayNames'] as const;

const config: AdminListPageConfig<DischargeReason<AdminDischargeReasonIndexFields<(typeof INCLUDES)[number]>>> = {
  title: 'Discharge Dispositions',
  columnStorageKey: 'admin.dischargeReasons',
  queryKey: adminDischargeReasonsQueryKeys.index,
  fetchFn: adminIndexDischargeReasons,
  fetchParams: {
    defaultSort: 'displayName asc',
  },
  searchPlaceholder: 'Search Dispositions',
  showResultCount: true,
  includes: INCLUDES,

  addButton: {
    label: 'Add Disposition',
    path: '/admin/discharge-reasons/new',
    permission: 'adminDischargeReasonCreate',
  },

  filters: [
    {
      key: 'dischargedToGroupType',
      label: 'Linked Group Type',
      type: 'multi_select',
      fetchConfig: {
        queryKey: ['adminGroupTypes', 'dischargeReasons', 'linkedGroupType', 'filter'],
        fetchFn: indexGroupTypes,
        params: { sortBy: 'displayName asc' },
      },
      prependedOptions: [{ displayName: 'None', id: 'null' }],
      getOptionLabel: (o) => o.displayName,
      getOptionValue: (o) => o.id,
    },
    {
      key: 'groupType',
      label: 'Displayed To',
      type: 'multi_select',
      fetchConfig: {
        queryKey: ['adminGroupTypes', 'dischargeReasons', 'displayedTo', 'filter'],
        fetchFn: indexGroupTypes,
        params: { sortBy: 'displayName asc', type: 'provider' },
      },
      prependedOptions: [{ displayName: 'None', id: 'null' }],
      getOptionLabel: (o) => o.displayName,
      getOptionValue: (o) => o.id,
    },
  ],

  columns: [
    {
      id: 'displayName',
      header: 'Discharge Disposition',
      render: (row) => <BoldText>{row.displayName || <>&mdash;</>}</BoldText>,
    },
    {
      id: 'dischargedToGroupType',
      header: 'Linked Group Type',
      sortable: false,
      render: (row) => row.dischargedToGroupType?.displayName || <>&mdash;</>,
    },
    {
      id: 'groupTypeDisplayNames',
      header: 'Displayed To',
      sortable: false,
      render: (row) => row.groupTypeDisplayNames || <>&mdash;</>,
    },
  ],

  rowActions: [
    {
      label: 'Edit',
      permission: 'adminDischargeReasonEdit',
      onClick: (row, { navigate }) => navigate(`/admin/discharge-reasons/${row.id}/edit`),
    },
    {
      label: 'Archive',
      danger: true,
      permission: 'adminDischargeReasonEdit',
      onClick: (row, { openModal }) => openModal('archive', row),
    },
  ],

  modals: {
    archive: {
      actionText: 'Archive',
      objType: 'disposition',
      onConfirm: async (row) => {
        await adminArchiveDischargeReason(row.id);
      },
    },
  },
};

export default function DischargeReasons() {
  const state = useAdminListPage(config);
  return <AdminListPageRenderer config={config} state={state} />;
}

const BoldText = styled.span`
  font-weight: 700;
`;
